import { CommonModule } from "@angular/common";
import { Component, Input } from "@angular/core";
import { formatRevisionMoneyDisplayLabel, type RevisionReviewTag, type RevisionReviewView } from "@handshake/core-types";

@Component({
  selector: "core-revision-review",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./core-revision-review.component.html",
  styleUrl: "./core-revision-review.component.scss",
})
export class CoreRevisionReviewComponent {
  @Input({ required: true }) view!: RevisionReviewView;
  @Input() variant: "client" | "business" = "business";
  @Input() currency = "SGD";
  @Input() firmName = "";

  get isRecord(): boolean {
    return this.view.changedOnly === true;
  }

  get datesChanged(): boolean {
    return Boolean(this.view.projectDates.previousStartDate || this.view.projectDates.previousEndDate);
  }

  get changedMilestoneCount(): number {
    return this.view.milestones.filter((milestone) => milestone.tag).length;
  }

  get milestoneCountLine(): string {
    const total = this.view.milestones.length;
    const changed = this.changedMilestoneCount;
    if (this.variant === "client") return `${total} in total, ${changed} changed`;
    return `${total} milestones, ${changed} changed`;
  }

  money(value: number | null | undefined): string {
    return (Number(value) || 0).toFixed(2);
  }

  taggedMoney(value: number | null | undefined): string {
    return `${this.currency} ${this.money(value)}`;
  }

  signedMoney(value: number | null | undefined): string {
    const amount = Number(value) || 0;
    const formatted = this.money(Math.abs(amount));
    if (amount > 0) return `+${formatted}`;
    if (amount < 0) return `-${formatted}`;
    return formatted;
  }

  dateLabel(value: string | null | undefined): string {
    if (!value) return "—";
    const parts = value.split("-");
    if (parts.length !== 3) return value;
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = months[Number(parts[1]) - 1];
    const day = String(Number(parts[2]));
    return month ? `${day} ${month} ${parts[0]}` : value;
  }

  tagLabel(tag: RevisionReviewTag | null | undefined): string {
    if (tag === "changed") return "Changed";
    if (tag === "new") return "New";
    if (tag === "deleted") return "Deleted";
    return "";
  }

  moneyStateLabel(label: string | null | undefined): string {
    return formatRevisionMoneyDisplayLabel(label);
  }

  pushedNote(days: number | null | undefined): string | null {
    if (days == null || days === 0) return null;
    if (days > 0) return `Pushed back by ${days} days`;
    return `Brought forward by ${Math.abs(days)} days`;
  }

  netChangeClass(net: number): string {
    if (net > 0) return "amount-increase";
    if (net < 0) return "amount-decrease";
    return "";
  }
}
